(() => {
  // apps/arcade/reward-policy.mjs
  var REWARD_POLICY = Object.freeze({ version: "arcade-credits-v2", creditsPerCent: 10, creditsPerDollar: 1e3, dailyCredits: 2e3, redemptionPercent: 10, timeZone: "America/New_York" });
  var integer = (n) => {
    if (!Number.isSafeInteger(n) || n < 0) throw new RangeError("Expected a non-negative safe integer");
    return n;
  };
  function creditsToCents(credits) {
    return Math.floor(integer(credits) / REWARD_POLICY.creditsPerCent);
  }
  function formatCredits(credits) {
    return "$" + (creditsToCents(credits) / 100).toFixed(2);
  }
  function flipRewardCredits(score) {
    return integer(score);
  }

  // apps/arcade/engine.mjs
  var FLAVORS = ["traditional", "pepper-crusted", "spicy-peri", "black-truffle", "chili-bites"];
  var COLORS = ["#4ec1c3", "#cd6ad4", "#ff934b", "#f4c430", "#ed6b5e"];
  var WORLDS = ["The first bite", "Pepper playground", "Peri pursuit", "Golden hour", "Chili chase", "The flavor vault"];
  var LIMITS = { run: 12, flip: 8, candy: 10 };
  var DIRS = [[0, -1], [1, 0], [0, 1], [-1, 0]];
  function mazePace(level2, fast = false) {
    return { player: Math.max(0.19, 0.3 - (level2 - 1) * 9e-3) * (fast ? 0.78 : 1), monster: Math.max(0.48, 0.82 - (level2 - 1) * 0.027) };
  }
  function random(seed) {
    return () => {
      seed |= 0;
      seed = seed + 1831565813 | 0;
      let t = Math.imul(seed ^ seed >>> 15, 1 | seed);
      t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }
  function shuffle(a, r) {
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(r() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }
  var key = (x, y) => `${x},${y}`;
  function flood(grid, blocked) {
    const q = [[1, 1]], seen = /* @__PURE__ */ new Set(["1,1"]);
    for (let i = 0; i < q.length; i++) for (const [dx, dy] of DIRS) {
      const x = q[i][0] + dx, y = q[i][1] + dy, k = key(x, y);
      if (grid[y]?.[x] === 0 && k !== blocked && !seen.has(k)) {
        seen.add(k);
        q.push([x, y]);
      }
    }
    return seen;
  }
  function addBypasses(grid) {
    const n = grid.length;
    for (let iteration = 0; iteration < n * n; iteration++) {
      const floor = [];
      for (let y = 1; y < n - 1; y++) for (let x = 1; x < n - 1; x++) if (!grid[y][x]) floor.push([x, y]);
      let changed = false;
      for (const [ax, ay] of floor) {
        if (ax === 1 && ay === 1) continue;
        const seen = flood(grid, key(ax, ay));
        if (seen.size === floor.length - 1) continue;
        const walls = [];
        for (let y = 1; y < n - 1; y++) for (let x = 1; x < n - 1; x++) if (grid[y][x]) {
          const sides = DIRS.map(([dx, dy]) => [x + dx, y + dy]).filter(([px, py]) => grid[py]?.[px] === 0 && !(px === ax && py === ay));
          if (sides.some((p) => seen.has(key(...p))) && sides.some((p) => !seen.has(key(...p)))) walls.push([x, y]);
        }
        walls.sort((a, b) => Math.abs(a[0] - ax) + Math.abs(a[1] - ay) - (Math.abs(b[0] - ax) + Math.abs(b[1] - ay)));
        if (walls.length) {
          const [x, y] = walls[0];
          grid[y][x] = 0;
          changed = true;
          break;
        }
      }
      if (!changed) break;
    }
  }
  function maze(level2) {
    const n = level2 < 5 ? 13 : 15, r = random(level2 * 319 + 7), grid = Array.from({ length: n }, () => Array(n).fill(1));
    const stack = [[1, 1]];
    grid[1][1] = 0;
    while (stack.length) {
      const [x, y] = stack.at(-1), d = shuffle([...DIRS], r).find(([dx2, dy2]) => x + dx2 * 2 > 0 && y + dy2 * 2 > 0 && x + dx2 * 2 < n - 1 && y + dy2 * 2 < n - 1 && grid[y + dy2 * 2][x + dx2 * 2]);
      if (!d) {
        stack.pop();
        continue;
      }
      const [dx, dy] = d;
      grid[y + dy][x + dx] = 0;
      grid[y + dy * 2][x + dx * 2] = 0;
      stack.push([x + dx * 2, y + dy * 2]);
    }
    for (let y = 1; y < n - 1; y++) for (let x = 1; x < n - 1; x++) if (grid[y][x] && (!grid[y][x - 1] && !grid[y][x + 1] || !grid[y - 1][x] && !grid[y + 1][x]) && r() < 0.32) grid[y][x] = 0;
    const floor = [];
    for (let y = 1; y < n - 1; y++) for (let x = 1; x < n - 1; x++) if (!grid[y][x] && (x !== 1 || y !== 1)) floor.push(key(x, y));
    const power = shuffle([...floor].filter((p) => {
      const [x, y] = p.split(",").map(Number);
      return x + y > 5;
    }), r).slice(0, 5);
    const pellets = floor.filter((p) => !power.includes(p));
    const far = [...floor].sort((a, b) => b.split(",").map(Number).reduce((v, n2) => v + n2, 0) - a.split(",").map(Number).reduce((v, n2) => v + n2, 0));
    const spawns = [far[0]];
    while (spawns.length < Math.min(4, 3 + Math.floor((level2 - 1) / 4))) {
      const candidates = far.filter((p) => !spawns.includes(p) && p.split(",").map(Number).reduce((a, b) => a + b, 0) > 8);
      candidates.sort((a, b) => {
        const distance = (p) => Math.min(...spawns.map((s) => {
          const [x, y] = s.split(",").map(Number), [px, py] = p.split(",").map(Number);
          return Math.abs(x - px) + Math.abs(y - py);
        }));
        return distance(b) - distance(a);
      });
      spawns.push(candidates[0]);
    }
    addBypasses(grid);
    for (let y = 1; y < n - 1; y++) for (let x = 1; x < n - 1; x++) if (!grid[y][x] && !(x === 1 && y === 1) && !floor.includes(key(x, y))) pellets.push(key(x, y));
    return { grid, pellets, power: power.map((p, i) => ({ p, flavor: i })), enemies: spawns.map((p) => p.split(",").map(Number)) };
  }
  function initMazeTreats(run2) {
    if (!Number.isFinite(run2.nextTreat)) run2.nextTreat = run2.elapsed + 18;
    if (!Number.isInteger(run2.treatCount) || run2.treatCount < 0) run2.treatCount = 0;
    if (run2.treat && (!["steak", "logo"].includes(run2.treat.kind) || !Number.isFinite(run2.treat.until) || typeof run2.treat.p !== "string" || run2.grid[+run2.treat.p.split(",")[1]]?.[+run2.treat.p.split(",")[0]] !== 0)) run2.treat = null;
    if (!run2.starterSteakPlaced) {
      const q = [run2.player], seen = /* @__PURE__ */ new Set([key(...run2.player)]);
      let spot = null;
      for (let i = 0; i < q.length; i++) {
        const p = q[i];
        if (i >= 2 && !run2.power.some((t) => t.p === key(...p)) && !run2.enemies.some((e) => key(...e) === key(...p))) {
          spot = key(...p);
          break;
        }
        for (const [dx, dy] of DIRS) {
          const np = [p[0] + dx, p[1] + dy], k = key(...np);
          if (run2.grid[np[1]]?.[np[0]] === 0 && !seen.has(k)) {
            seen.add(k);
            q.push(np);
          }
        }
      }
      if (spot) run2.treat = { kind: "steak", p: spot, until: run2.elapsed + 60 };
      run2.starterSteakPlaced = true;
    }
  }
  function updateMazeTreats(run2) {
    if (run2.treat && run2.elapsed >= run2.treat.until) run2.treat = null;
    if (run2.treat || run2.elapsed < run2.nextTreat) return;
    const candidates = [...flood(run2.grid, "")].filter((p) => p !== key(...run2.player) && !run2.power.some((t) => t.p === p) && !run2.enemies.some((e) => key(...e) === p));
    candidates.sort((a, b) => {
      const distance = (p) => {
        const [x, y] = p.split(",").map(Number);
        return Math.abs(x - run2.player[0]) + Math.abs(y - run2.player[1]);
      };
      return Math.abs(distance(a) - 4) - Math.abs(distance(b) - 4);
    });
    if (candidates.length) {
      run2.treat = { kind: run2.treatCount % 2 === 0 ? "logo" : "steak", p: candidates[0], until: run2.elapsed + 16 };
      run2.treatCount++;
    }
    run2.nextTreat = run2.elapsed + 24;
  }
  function collectMazeTreat(run2) {
    if (!run2.treat || run2.treat.until <= run2.elapsed || run2.treat.p !== key(...run2.player)) return null;
    const kind = run2.treat.kind;
    run2.treat = null;
    run2.nextTreat = run2.elapsed + 18;
    if (kind === "logo") run2.score += 100;
    else {
      pouchPower(run2, 0);
    }
    return kind;
  }
  function pouchPower(run2, flavor) {
    run2.effect = flavor;
    run2.powerTime = flavor === 0 ? 10 : 8;
    run2.eatCombo = 0;
    run2.score += 50;
  }
  function monsterContact(run2) {
    const eaten = [];
    for (let i = 0; i < run2.enemies.length; i++) {
      if ((run2.enemySleep?.[i] || 0) > 0) continue;
      const p = run2.enemies[i];
      if (p[0] !== run2.player[0] || p[1] !== run2.player[1]) continue;
      if (run2.powerTime > 0) {
        run2.eatCombo = (run2.eatCombo || 0) + 1;
        const points = 200 * Math.min(run2.eatCombo, 4);
        run2.score += points;
        run2.enemySleep ??= run2.enemies.map(() => 0);
        run2.enemySleep[i] = 4;
        eaten.push({ index: i, points });
      } else if (run2.invincible <= 0) return { hit: true, eaten };
    }
    return { hit: false, eaten };
  }
  function fleeStep(grid, start, target, index = 0) {
    const available = DIRS.map(([dx, dy]) => [start[0] + dx, start[1] + dy]).filter(([x, y]) => grid[y]?.[x] === 0);
    available.sort((a, b) => Math.abs(b[0] - target[0]) + Math.abs(b[1] - target[1]) - (Math.abs(a[0] - target[0]) + Math.abs(a[1] - target[1])));
    const best = available.filter((p) => Math.abs(p[0] - target[0]) + Math.abs(p[1] - target[1]) === Math.abs(available[0]?.[0] - target[0]) + Math.abs(available[0]?.[1] - target[1]));
    return best[index % best.length] || start;
  }
  function nextStep(grid, start, target) {
    const q = [[...start]], seen = /* @__PURE__ */ new Set([key(...start)]), prev = /* @__PURE__ */ new Map();
    let end = null;
    for (let i = 0; i < q.length; i++) {
      const p = q[i];
      if (p[0] === target[0] && p[1] === target[1]) {
        end = key(...p);
        break;
      }
      for (const [dX, dY] of DIRS) {
        const x = p[0] + dX, y = p[1] + dY, k = key(x, y);
        if (grid[y]?.[x] === 0 && !seen.has(k)) {
          seen.add(k);
          prev.set(k, key(...p));
          q.push([x, y]);
        }
      }
    }
    const origin = key(...start);
    if (!end || end === origin) return start;
    while (prev.get(end) !== origin) end = prev.get(end);
    return end.split(",").map(Number);
  }
  function flipLevel(level2) {
    const pairs = Math.min(10, 3 + level2), r = random(level2 * 149 + 13);
    return { cards: shuffle(Array.from({ length: pairs * 2 }, (_, i) => Math.floor(i / 2)), r), pairs, target: pairs + Math.max(3, 9 - level2) };
  }
  function emptyProfile() {
    return { v: 1, name: "Snack explorer", run: { unlocked: 1, stars: {}, best: 0 }, flip: { unlocked: 1, stars: {}, best: 0 }, candy: { unlocked: 1, stars: {}, best: 0 } };
  }
  function validateProfile(p) {
    if (!p || p.v !== 1 || typeof p.name !== "string") throw Error("That is not a Joburg Arcade progress code.");
    const out = emptyProfile();
    out.name = p.name.trim().slice(0, 24) || "Snack explorer";
    for (const game2 of Object.keys(LIMITS)) {
      const s = p[game2] || (game2 === "candy" ? out.candy : null);
      if (!s || !Number.isInteger(s.unlocked) || s.unlocked < 1 || s.unlocked > LIMITS[game2]) throw Error("Invalid level data.");
      out[game2].unlocked = s.unlocked;
      out[game2].best = Number.isInteger(s.best) ? Math.max(0, Math.min(1e7, s.best)) : 0;
      for (const [l, v] of Object.entries(s.stars || {})) if (Number.isInteger(+l) && +l >= 1 && +l <= s.unlocked && +l <= LIMITS[game2] && Number.isInteger(v) && v >= 1 && v <= 3) out[game2].stars[l] = v;
    }
    return out;
  }
  function complete(p, game2, level2, stars, score) {
    if (!LIMITS[game2] || !Number.isInteger(level2) || level2 < 1 || level2 > p[game2].unlocked || level2 > LIMITS[game2]) throw Error("Level is locked.");
    p[game2].stars[level2] = Math.max(p[game2].stars[level2] || 0, stars);
    p[game2].unlocked = Math.max(p[game2].unlocked, Math.min(LIMITS[game2], level2 + 1));
    p[game2].best = Math.max(p[game2].best, score);
    return p;
  }
  function flipCredits(score) {
    return Number.isSafeInteger(score) && score >= 0 ? flipRewardCredits(score) : 0;
  }

  // apps/arcade/drawing.mjs
  function steak(ctx2, x, y, size, angle = -0.35) {
    ctx2.save();
    ctx2.translate(x, y);
    ctx2.rotate(angle);
    ctx2.scale(size * 1.12, size * 0.73);
    ctx2.shadowColor = "#0006";
    ctx2.shadowBlur = 3;
    ctx2.shadowOffsetY = 2;
    ctx2.beginPath();
    ctx2.moveTo(-0.47, -0.15);
    ctx2.bezierCurveTo(-0.38, -0.47, 0.03, -0.48, 0.32, -0.28);
    ctx2.bezierCurveTo(0.56, -0.08, 0.51, 0.28, 0.23, 0.4);
    ctx2.bezierCurveTo(-0.02, 0.5, -0.57, 0.2, -0.47, -0.15);
    ctx2.fillStyle = "#af724c";
    ctx2.fill();
    ctx2.shadowBlur = 0;
    ctx2.shadowOffsetY = 0;
    ctx2.lineWidth = 0.055;
    ctx2.strokeStyle = "#47271c";
    ctx2.stroke();
    ctx2.scale(0.87, 0.84);
    ctx2.beginPath();
    ctx2.moveTo(-0.44, -0.12);
    ctx2.bezierCurveTo(-0.3, -0.42, 0.08, -0.37, 0.3, -0.23);
    ctx2.bezierCurveTo(0.53, 0, 0.39, 0.24, 0.18, 0.32);
    ctx2.bezierCurveTo(-0.06, 0.38, -0.49, 0.18, -0.44, -0.12);
    const g = ctx2.createLinearGradient(-0.4, -0.4, 0.3, 0.4);
    g.addColorStop(0, "#b56544");
    g.addColorStop(1, "#743524");
    ctx2.fillStyle = g;
    ctx2.fill();
    ctx2.lineCap = "round";
    ctx2.strokeStyle = "#dfb190";
    ctx2.lineWidth = 0.023;
    for (let i = 0; i < 3; i++) {
      const dx = -0.25 + i * 0.18;
      ctx2.beginPath();
      ctx2.moveTo(dx, -0.21);
      ctx2.bezierCurveTo(dx + 0.1, -0.08, dx - 0.07, 0.03, dx + 0.1, 0.21);
      ctx2.stroke();
    }
    ctx2.fillStyle = "#f5cb96";
    for (const [px, py] of [[-0.22, -0.24], [0.14, 0.19], [0.24, -0.06]]) {
      ctx2.beginPath();
      ctx2.arc(px, py, 0.025, 0, Math.PI * 2);
      ctx2.fill();
    }
    ctx2.restore();
  }
  function renderMaze(ctx2, run2, art2, visual2, brandLogo2) {
    const c = 720 / run2.grid.length, clock = run2.elapsed, reduce = visual2.reduced;
    const rounded = (x2, y2, w, h, r, fill) => {
      ctx2.fillStyle = fill;
      ctx2.beginPath();
      ctx2.roundRect(x2, y2, w, h, r);
      ctx2.fill();
    };
    ctx2.fillStyle = "#102824";
    ctx2.fillRect(0, 0, 720, 720);
    for (let y2 = 0; y2 < run2.grid.length; y2++) for (let x2 = 0; x2 < run2.grid.length; x2++) if (run2.grid[y2][x2]) {
      rounded(x2 * c + 2, y2 * c + 2, c - 4, c - 4, 8, "#285a51");
      rounded(x2 * c + 5, y2 * c + 4, c - 10, 3, 1, "#538577");
    }
    for (const p2 of run2.pellets) {
      const [x2, y2] = p2.split(",").map(Number);
      steak(ctx2, (x2 + 0.5) * c, (y2 + 0.5) * c, c * 0.48, ((x2 + y2) % 3 - 1) * 0.28);
    }
    for (const p2 of run2.power) {
      const [x2, y2] = p2.p.split(",").map(Number), bob = reduce ? 0 : Math.sin(clock * 3 + p2.flavor) * c * 0.035;
      ctx2.save();
      ctx2.translate((x2 + 0.5) * c, (y2 + 0.5) * c + bob);
      ctx2.shadowColor = COLORS[p2.flavor];
      ctx2.shadowBlur = reduce ? 4 : 10 + 3 * Math.sin(clock * 3);
      rounded(-c * 0.43, -c * 0.46, c * 0.86, c * 0.92, 8, COLORS[p2.flavor]);
      ctx2.shadowBlur = 0;
      const im = art2[p2.flavor];
      if (im.complete && im.naturalWidth) ctx2.drawImage(im, -c * 0.35, -c * 0.42, c * 0.7, c * 0.84);
      ctx2.strokeStyle = "#fff4cc";
      ctx2.lineWidth = 1.5;
      ctx2.beginPath();
      ctx2.moveTo(c * 0.32, -c * 0.43);
      ctx2.lineTo(c * 0.48, -c * 0.43);
      ctx2.moveTo(c * 0.4, -c * 0.51);
      ctx2.lineTo(c * 0.4, -c * 0.35);
      ctx2.stroke();
      ctx2.restore();
    }
    if (run2.treat) {
      const [x2, y2] = run2.treat.p.split(",").map(Number), px = (x2 + 0.5) * c, py = (y2 + 0.5) * c;
      ctx2.save();
      ctx2.shadowColor = "#ffe27c";
      ctx2.shadowBlur = 12;
      rounded(px - c * 0.46, py - c * 0.4, c * 0.92, c * 0.8, 8, "#fff0be");
      ctx2.shadowBlur = 0;
      if (run2.treat.kind === "steak") steak(ctx2, px, py, c * 0.88, -0.2);
      else if (brandLogo2?.complete && brandLogo2.naturalWidth) ctx2.drawImage(brandLogo2, px - c * 0.42, py - c * 0.3, c * 0.84, c * 0.42);
      ctx2.fillStyle = "#5d3515";
      ctx2.font = `bold ${c * 0.17}px Arial`;
      ctx2.textAlign = "center";
      ctx2.fillText(run2.treat.kind === "steak" ? "POWER" : "+100", px, py + c * 0.3);
      ctx2.restore();
    }
    run2.enemies.forEach((p2, i) => {
      const point = visual2.enemies[i] || p2, x2 = (point[0] + 0.5) * c, y2 = (point[1] + 0.5) * c, sleep = run2.enemySleep?.[i] || 0;
      ctx2.save();
      ctx2.translate(x2, y2);
      if (sleep > 0) {
        ctx2.strokeStyle = "#d3dbc578";
        ctx2.lineWidth = 2;
        ctx2.beginPath();
        ctx2.arc(0, 0, c * 0.24, 0, Math.PI * 2);
        ctx2.stroke();
        ctx2.fillStyle = "#d3dbc5";
        ctx2.font = `bold ${c * 0.26}px Arial`;
        ctx2.textAlign = "center";
        ctx2.fillText(Math.ceil(sleep), 0, c * 0.09);
        ctx2.restore();
        return;
      }
      const scared = run2.powerTime > 0, bob = reduce ? 0 : Math.sin(clock * 8 + i * 2) * c * 0.025;
      ctx2.translate(0, bob);
      const color = scared ? "#bddbeb" : ["#ca86de", "#f2c93c", "#ee806b", "#8abddd"][i];
      ctx2.fillStyle = "#0004";
      ctx2.beginPath();
      ctx2.ellipse(0, c * 0.35, c * 0.31, c * 0.075, 0, 0, 7);
      ctx2.fill();
      const skin2 = ctx2.createRadialGradient(-c * 0.13, -c * 0.17, 0, c * 0.02, c * 0.1, c * 0.48);
      skin2.addColorStop(0, scared ? "#f1faff" : ["#eec4fb", "#fff4a7", "#ffc7b2", "#c4eafb"][i]);
      skin2.addColorStop(0.5, color);
      skin2.addColorStop(1, scared ? "#739eae" : ["#784d99", "#aa7723", "#ad4c57", "#537da1"][i]);
      ctx2.shadowColor = "#0004";
      ctx2.shadowBlur = 5;
      rounded(-c * 0.32, -c * 0.3, c * 0.64, c * 0.63, [c * 0.24, c * 0.24, c * 0.15, c * 0.15], skin2);
      ctx2.shadowBlur = 0;
      ctx2.strokeStyle = scared ? "#e7f9ff" : "#10282488";
      ctx2.lineWidth = 1.5;
      ctx2.stroke();
      ctx2.fillStyle = color;
      ctx2.beginPath();
      ctx2.moveTo(-c * 0.26, -c * 0.15);
      ctx2.lineTo(-c * 0.3, -c * 0.42);
      ctx2.lineTo(-c * 0.08, -c * 0.25);
      ctx2.moveTo(c * 0.1, -c * 0.25);
      ctx2.lineTo(c * 0.29, -c * 0.41);
      ctx2.lineTo(c * 0.27, -c * 0.12);
      ctx2.fill();
      rounded(-c * 0.27, c * 0.23, c * 0.19, c * 0.13, 5, skin2);
      rounded(c * 0.09, c * 0.23, c * 0.19, c * 0.13, 5, skin2);
      const eyeDx = Math.sign((visual2.player || run2.player)[0] - point[0]) * c * 0.025, eyeDy = Math.sign((visual2.player || run2.player)[1] - point[1]) * c * 0.02;
      for (const ex of [-0.12, 0.13]) {
        ctx2.fillStyle = "#40314944";
        ctx2.beginPath();
        ctx2.ellipse(ex * c, -c * 0.04, c * 0.126, c * 0.135, 0, 0, 7);
        ctx2.fill();
        ctx2.fillStyle = "white";
        ctx2.beginPath();
        ctx2.ellipse(ex * c, -c * 0.065, c * 0.109, c * (scared ? 0.14 : 0.115), 0, 0, 7);
        ctx2.fill();
        ctx2.fillStyle = "#142e27";
        ctx2.beginPath();
        ctx2.arc(ex * c + eyeDx, -c * 0.04 + eyeDy, c * 0.052, 0, 7);
        ctx2.fill();
        ctx2.fillStyle = "#fff";
        ctx2.beginPath();
        ctx2.arc(ex * c + eyeDx - c * 0.017, -c * 0.06 + eyeDy, c * 0.018, 0, 7);
        ctx2.fill();
      }
      ctx2.strokeStyle = "#14382f";
      ctx2.lineWidth = 2;
      ctx2.beginPath();
      if (scared) {
        ctx2.arc(0, c * 0.18, c * 0.065, 0, 7);
      } else {
        ctx2.moveTo(-c * 0.12, c * 0.13);
        ctx2.quadraticCurveTo(0, c * 0.21, c * 0.12, c * 0.13);
      }
      ctx2.stroke();
      if (scared) {
        ctx2.fillStyle = "#fff";
        ctx2.font = `bold ${c * 0.2}px Arial`;
        ctx2.textAlign = "center";
        ctx2.fillText("!", 0, -c * 0.4);
      }
      ctx2.restore();
    });
    const p = visual2.player || run2.player, x = (p[0] + 0.5) * c, y = (p[1] + 0.5) * c;
    ctx2.save();
    ctx2.translate(x, y);
    const powered = run2.powerTime > 0;
    if (powered || run2.invincible > 0) {
      ctx2.strokeStyle = powered ? "#f9d766" : "#f9f5c9";
      ctx2.lineWidth = powered ? 3.5 : 2;
      ctx2.beginPath();
      ctx2.arc(0, 0, c * 0.44, 0, 7);
      ctx2.stroke();
    }
    const breathe = reduce ? 0 : Math.sin(clock * 3) * 0.014, stretch = powered ? 1.06 : 1;
    ctx2.scale(stretch * (1 + breathe), stretch * (1 - breathe));
    ctx2.fillStyle = "#0005";
    ctx2.beginPath();
    ctx2.ellipse(0, c * 0.35, c * 0.32, c * 0.08, 0, 0, 7);
    ctx2.fill();
    const scarfWave = reduce ? 0 : Math.sin(clock * 5) * c * 0.035;
    ctx2.fillStyle = "#299b9f";
    ctx2.beginPath();
    ctx2.moveTo(-c * 0.25, c * 0.16);
    ctx2.bezierCurveTo(-c * 0.38, c * 0.08, -c * 0.42, c * 0.23 + scarfWave, -c * 0.47, c * 0.13 + scarfWave);
    ctx2.lineTo(-c * 0.43, c * 0.3);
    ctx2.quadraticCurveTo(-c * 0.33, c * 0.33, -c * 0.23, c * 0.27);
    ctx2.fill();
    const skin = ctx2.createRadialGradient(-c * 0.12, -c * 0.2, c * 0.015, c * 0.07, c * 0.09, c * 0.48);
    skin.addColorStop(0, "#ffe6a6");
    skin.addColorStop(0.38, "#ffba55");
    skin.addColorStop(0.72, "#f37c27");
    skin.addColorStop(1, "#b8441e");
    ctx2.shadowColor = "#0005";
    ctx2.shadowBlur = 6;
    ctx2.beginPath();
    ctx2.moveTo(-c * 0.32, c * 0.08);
    ctx2.bezierCurveTo(-c * 0.43, -c * 0.23, -c * 0.19, -c * 0.4, c * 0.08, -c * 0.34);
    ctx2.bezierCurveTo(c * 0.33, -c * 0.33, c * 0.43, -c * 0.07, c * 0.31, c * 0.18);
    ctx2.quadraticCurveTo(c * 0.21, c * 0.36, 0, c * 0.32);
    ctx2.quadraticCurveTo(-c * 0.29, c * 0.35, -c * 0.32, c * 0.08);
    ctx2.fillStyle = skin;
    ctx2.fill();
    ctx2.shadowBlur = 0;
    ctx2.strokeStyle = "#a4482480";
    ctx2.lineWidth = 1.3;
    ctx2.stroke();
    ctx2.strokeStyle = "#fff0b6aa";
    ctx2.lineWidth = 2;
    ctx2.beginPath();
    ctx2.moveTo(-c * 0.23, -c * 0.22);
    ctx2.quadraticCurveTo(-c * 0.1, -c * 0.33, c * 0.035, -c * 0.28);
    ctx2.stroke();
    const look = visual2.direction === 1 ? 0.035 : visual2.direction === 3 ? -0.035 : 0;
    const blink = !reduce && clock % 5 > 4.82;
    for (const ex of [-0.105, 0.135]) {
      ctx2.fillStyle = "#8c471c40";
      ctx2.beginPath();
      ctx2.ellipse(ex * c, -c * 0.06, c * 0.105, c * 0.13, 0, 0, 7);
      ctx2.fill();
      ctx2.fillStyle = "#fffdf0";
      ctx2.beginPath();
      ctx2.ellipse(ex * c, -c * 0.09, c * 0.096, c * (blink ? 0.025 : 0.12), 0, 0, 7);
      ctx2.fill();
      if (!blink) {
        ctx2.fillStyle = "#25423c";
        ctx2.beginPath();
        ctx2.ellipse((ex + look) * c, -c * 0.065, c * 0.052, c * 0.067, 0, 0, 7);
        ctx2.fill();
        ctx2.fillStyle = "#fff";
        ctx2.beginPath();
        ctx2.arc((ex + look - 0.018) * c, -c * 0.092, c * 0.022, 0, 7);
        ctx2.fill();
      }
    }
    ctx2.fillStyle = "#b345274d";
    for (const ex of [-0.22, 0.24]) {
      ctx2.beginPath();
      ctx2.ellipse(ex * c, c * 0.07, c * 0.06, c * 0.03, 0, 0, 7);
      ctx2.fill();
    }
    ctx2.fillStyle = "#743323";
    ctx2.beginPath();
    ctx2.ellipse(c * 0.015, c * 0.105, c * 0.12, c * (reduce ? 0.055 : 0.055 + 0.012 * Math.sin(clock * 7)), 0, 0, 7);
    ctx2.fill();
    ctx2.fillStyle = "#fff5de";
    rounded(-c * 0.065, c * 0.055, c * 0.15, c * 0.035, c * 0.015, "#fff5de");
    const fabric = ctx2.createLinearGradient(-c * 0.3, 0, c * 0.3, 0);
    fabric.addColorStop(0, "#1f8d94");
    fabric.addColorStop(0.45, "#85e6db");
    fabric.addColorStop(1, "#32a7af");
    rounded(-c * 0.3, c * 0.225, c * 0.6, c * 0.09, 4, fabric);
    ctx2.fillStyle = "#baf6e7";
    ctx2.beginPath();
    ctx2.arc(c * 0.19, c * 0.27, c * 0.055, 0, 7);
    ctx2.fill();
    ctx2.restore();
    for (const pop2 of visual2.pops) {
      const t = pop2.age / pop2.life;
      ctx2.save();
      ctx2.globalAlpha = 1 - t;
      ctx2.fillStyle = pop2.color;
      ctx2.font = `900 ${c * 0.32}px Arial`;
      ctx2.textAlign = "center";
      ctx2.strokeStyle = "#102824";
      ctx2.lineWidth = 4;
      const px = (pop2.x + 0.5) * c, py = (pop2.y + 0.15) * c - (reduce ? 0 : t * c * 0.6);
      ctx2.strokeText(pop2.text, px, py);
      ctx2.fillText(pop2.text, px, py);
      ctx2.restore();
    }
    if (powered) {
      ctx2.fillStyle = "#f9d766";
      ctx2.fillRect(12, 708, 696 * Math.min(1, run2.powerTime / (run2.effect === 0 ? 10 : 8)), 4);
    }
  }

  // apps/arcade/candy.mjs
  var clamp = (n, a, b) => Math.max(a, Math.min(b, n));
  var CANDY_WORLDS = ["Jellybean junction", "The candy cloud", "Gummy galaxy", "Peppermint patrol", "Sugar storm", "The twist zone", "Neon nougat", "Bonbon barrage", "The final sprinkle", "King of the candy"];
  var CANDY_SCENES = [
    ["#172342", "#18454d", "#ef82b9", "#ffe18d", "drift", "Jellybeans drift gently. Learn to dodge."],
    ["#372445", "#794e78", "#d4baff", "#ffb7da", "chevron", "Cloud candies fly in a V formation."],
    ["#101c48", "#303878", "#91efb4", "#b8a3ff", "orbit", "Gummies orbit. Watch for diving candies."],
    ["#173b39", "#29615a", "#ff7b93", "#fff1d0", "zigzag", "Peppermints zigzag and fire paired sprinkles."],
    ["#3e2143", "#733251", "#ffab76", "#f2bafc", "storm", "Sugar crystals descend faster. Keep moving."],
    ["#322441", "#533e72", "#c998ff", "#f8a2c6", "twist", "Twists braid across the sky and dive."],
    ["#122e48", "#315750", "#6cf1d6", "#f4a1ee", "neon", "Armored nougat takes extra hits."],
    ["#43243b", "#79524a", "#ffd094", "#e6a0cf", "diamond", "Bonbons form a diamond. Dodge the spread."],
    ["#27214e", "#4f375f", "#afc4ff", "#ffb99d", "spiral", "Sprinkles spiral. A two-phase boss awaits."],
    ["#241833", "#645223", "#ffd66d", "#e0b5ff", "crown", "The Candy King combines dives, armor and fans."]
  ];
  var scene = (s) => CANDY_SCENES[s.level - 1];
  function candyWave(s) {
    s.enemies = [];
    s.bullets = [];
    s.drops = [];
    s.waveTime = 0;
    s.spawnTime = 1.8;
    const cols = 5, rows = 2 + Math.floor((s.level - 1) / 4), boss = s.wave === s.waves && (s.level % 3 === 0 || s.level === 10);
    for (let row = 0; row < rows; row++) for (let col = 0; col < cols; col++) {
      const offset = s.level === 2 ? Math.abs(col - 2) * 22 : s.level === 8 ? Math.abs(col - 2) * -18 : s.level === 10 ? col % 2 * 24 : 0;
      const hp = 1 + (row === 0 && s.level >= 4 ? 1 : 0) + (s.level >= 7 && col % 2 === 0 ? 1 : 0);
      s.enemies.push({ x: 132 + col * 112, y: (boss ? 175 : 105) + row * (boss ? 50 : 67) + offset, home: 132 + col * 112, row, col, hp, maxHp: hp, kind: (col + row + s.wave) % 3, boss: false, dive: 0 });
    }
    if (boss) s.enemies.push({ x: 360, y: 100, home: 360, row: -1, col: 2, hp: 12 + s.level * 2, maxHp: 12 + s.level * 2, kind: 2, boss: true, dive: 0 });
    s.banner = 2;
  }
  function newCandy(level2) {
    if (!Number.isInteger(level2) || level2 < 1 || level2 > 10) throw Error("Invalid candy level");
    const s = { v: 2, level: level2, wave: 1, waves: 2 + Math.floor((level2 - 1) / 3), player: { x: 360, y: 614 }, lives: 3, score: 0, kills: 0, elapsed: 0, waveTime: 0, fire: 0, spawnTime: 2, shield: 0, rapid: 0, invincible: 2, shots: [], bullets: [], enemies: [], drops: [], sparks: [], banner: 2, status: "playing" };
    candyWave(s);
    return s;
  }
  function restoreCandy(raw, level2) {
    if (!raw || ![1, 2].includes(raw.v) || raw.level !== level2 || !Number.isInteger(level2) || level2 < 1 || level2 > 10 || raw.status !== "playing") return null;
    const s = JSON.parse(JSON.stringify(raw)), base = newCandy(level2);
    for (const name of ["score", "kills", "elapsed", "waveTime", "fire", "spawnTime", "shield", "rapid", "invincible", "banner"]) if (!Number.isFinite(s[name]) || s[name] < 0 || s[name] > 1e8) return null;
    if (!Number.isInteger(s.lives) || s.lives < 1 || s.lives > 3 || !Number.isInteger(s.wave) || s.wave < 1 || s.wave > base.waves || s.waves !== base.waves) return null;
    if (!s.player || !Number.isFinite(s.player.x) || !Number.isFinite(s.player.y)) return null;
    for (const field of ["shots", "bullets", "enemies", "drops", "sparks"]) if (!Array.isArray(s[field]) || s[field].length > 200 || s[field].some((p) => !p || !Number.isFinite(p.x) || !Number.isFinite(p.y))) return null;
    if (s.enemies.some((e) => !Number.isFinite(e.hp) || e.hp <= 0 || !Number.isFinite(e.home) || !Number.isFinite(e.row) || !Number.isFinite(e.col) || !Number.isInteger(e.kind) || e.kind < 0 || e.kind > 2)) return null;
    if (s.shots.some((p) => !Number.isFinite(p.vx)) || s.bullets.some((p) => !Number.isFinite(p.vx) || !Number.isFinite(p.vy)) || s.drops.some((p) => !["shield", "rapid"].includes(p.kind))) return null;
    if (s.v === 2 && s.enemies.some((e) => !Number.isFinite(e.maxHp) || e.maxHp < e.hp || !Number.isFinite(e.dive) || e.dive < 0 || e.dive > 1e8)) return null;
    if (s.v === 1) s.enemies.forEach((e) => {
      e.maxHp = Math.max(e.hp, e.boss ? 8 + s.level : e.hp);
      e.dive = 0;
    });
    s.v = 2;
    s.player.x = clamp(s.player.x, 38, 682);
    s.player.y = clamp(s.player.y, 350, 663);
    s.invincible = Math.max(2, s.invincible);
    s.sparks = [];
    return s;
  }
  function burst(s, x, y, color) {
    for (let i = 0; i < 9; i++) {
      const a = i * Math.PI * 2 / 9;
      s.sparks.push({ x, y, vx: Math.cos(a) * 105, vy: Math.sin(a) * 105, life: 0.5, color });
    }
    s.sparks = s.sparks.slice(-180);
  }
  function damage(s) {
    if (s.invincible > 0 || s.shield > 0) return;
    s.lives--;
    s.invincible = 2;
    s.player.y = 630;
    burst(s, s.player.x, s.player.y, "#ff9b78");
    if (!s.lives) s.status = "lost";
  }
  function stepCandy(s, dt, input = {}) {
    if (s.status !== "playing") return s.status;
    dt = clamp(dt, 0, 0.06);
    s.elapsed += dt;
    s.waveTime += dt;
    for (const k of ["shield", "rapid", "invincible", "banner"]) s[k] = Math.max(0, s[k] - dt);
    let dx = input.x || 0, dy = input.y || 0;
    if (input.target) {
      dx = input.target.x - s.player.x;
      dy = input.target.y - s.player.y;
      const distance = Math.hypot(dx, dy);
      if (distance > 0) {
        const step = Math.min(distance, 340 * dt);
        s.player.x += dx / distance * step;
        s.player.y += dy / distance * step;
      }
    } else {
      const length = Math.hypot(dx, dy) || 1;
      s.player.x += dx / length * 340 * dt;
      s.player.y += dy / length * 340 * dt;
    }
    s.player.x = clamp(s.player.x, 38, 682);
    s.player.y = clamp(s.player.y, 350, 663);
    s.fire -= dt;
    if (s.fire <= 0) {
      s.fire = s.rapid > 0 ? 0.13 : 0.24;
      for (const vx of s.rapid > 0 ? [-100, 0, 100] : [0]) s.shots.push({ x: s.player.x, y: s.player.y - 36, vx });
    }
    for (const shot of s.shots) {
      shot.x += shot.vx * dt;
      shot.y -= 530 * dt;
      for (const e of s.enemies) {
        if (e.hp <= 0) continue;
        const r = e.boss ? 40 : 23;
        if (Math.abs(shot.x - e.x) < r && Math.abs(shot.y - e.y) < r) {
          e.hp--;
          shot.y = -100;
          burst(s, e.x, e.y, ["#f291d2", "#ffbc56", "#7ce4d4"][e.kind]);
          if (e.hp === 0) {
            s.kills++;
            s.score += e.boss ? 300 : e.kind === 2 ? 40 : 25;
            if (s.kills % 7 === 0) s.drops.push({ x: e.x, y: e.y, kind: s.kills % 14 === 0 ? "shield" : "rapid" });
          }
          break;
        }
      }
    }
    s.shots = s.shots.filter((p) => p.y > -30 && p.x > 0 && p.x < 720);
    s.enemies = s.enemies.filter((e) => e.hp > 0);
    for (const e of s.enemies) {
      const phase = e.boss && e.hp <= e.maxHp / 2 ? 2 : 1;
      const cycle = (s.waveTime + e.col * 0.7 + e.row * 1.3) % Math.max(6, 12 - s.level * 0.4);
      const canDive = !e.boss && s.level >= 3 && (e.col + e.row + s.wave) % 3 === 0;
      e.warning = canDive && !e.dive && s.waveTime > 3 && cycle > 4 && cycle < 5;
      if (canDive && !e.dive && s.waveTime > 3 && cycle >= 5 && cycle < 5.2) e.dive = 1e-3;
      if (e.dive) {
        e.dive += dt;
        e.y += (72 + s.level * 9) * dt;
        e.x = clamp(e.home + Math.sin(e.dive * 2.3) * 95, 40, 680);
      } else {
        const speed = 0.55 + s.level * 0.095, amplitude = e.boss ? 150 : 45 + s.level * 3;
        const wave = s.waveTime * speed + e.row * 0.7 + (s.level === 9 ? e.col * 0.6 : 0);
        const motion = s.level === 4 ? Math.asin(Math.sin(wave)) * 2 / Math.PI : Math.sin(wave);
        e.x = e.home + motion * (s.level === 4 ? 70 : amplitude) + (s.level === 6 ? Math.sin(wave * 2) * 24 : 0);
        if (e.boss) e.y = 100 + Math.sin(s.waveTime * 0.8) * 10;
        else e.y += (4 + s.level * 1.35 + s.wave * 0.6 + ([3, 9].includes(s.level) ? Math.cos(wave) * 12 : 0)) * dt;
      }
      e.phase = phase;
      if (e.y > 685) {
        e.hp = 0;
        damage(s);
      } else if (Math.abs(e.x - s.player.x) < 42 && Math.abs(e.y - s.player.y) < 45) {
        if (!e.boss) e.hp = 0;
        damage(s);
        burst(s, e.x, e.y, "#f4c430");
      }
    }
    s.enemies = s.enemies.filter((e) => e.hp > 0);
    s.spawnTime -= dt;
    if (s.spawnTime <= 0 && s.enemies.length) {
      s.spawnTime = Math.max(0.38, 1.65 - s.level * 0.115);
      const boss = s.enemies.find((e2) => e2.boss), e = boss && Math.floor(s.elapsed) % 2 === 0 ? boss : s.enemies[Math.floor(s.elapsed * 7) % s.enemies.length];
      const aim = clamp((s.player.x - e.x) * 0.45, -135, 135), phase = e.phase || 1;
      const spread = e.boss ? phase === 2 ? [-130, -65, 0, 65, 130] : [-85, 0, 85] : s.level >= 8 ? [-55, 0, 55] : s.level >= 4 ? [-28, 28] : [0];
      spread.forEach((v) => s.bullets.push({ x: e.x, y: e.y + 18, vx: aim + v, vy: 125 + s.level * 13 + (phase === 2 ? 22 : 0) }));
    }
    for (const b of s.bullets) {
      b.x += b.vx * dt;
      b.y += b.vy * dt;
      if (Math.abs(b.x - s.player.x) < 23 && Math.abs(b.y - s.player.y) < 32) {
        b.y = 800;
        damage(s);
      }
    }
    s.bullets = s.bullets.filter((b) => b.y < 750 && b.x > -20 && b.x < 740).slice(-180);
    for (const drop of s.drops) {
      drop.y += 110 * dt;
      if (Math.abs(drop.x - s.player.x) < 36 && Math.abs(drop.y - s.player.y) < 44) {
        s[drop.kind] = 8;
        drop.y = 800;
        burst(s, s.player.x, s.player.y, "#b6f3d9");
      }
    }
    s.drops = s.drops.filter((p) => p.y < 750);
    for (const p of s.sparks) {
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.life -= dt;
    }
    s.sparks = s.sparks.filter((p) => p.life > 0);
    if (!s.enemies.length && s.status === "playing") {
      if (s.wave === s.waves) s.status = "won";
      else {
        s.wave++;
        candyWave(s);
        s.invincible = Math.max(1, s.invincible);
      }
    }
    return s.status;
  }
  function candy(ctx2, x, y, kind, scale = 1, level2 = 1) {
    ctx2.save();
    ctx2.translate(x, y);
    ctx2.scale(scale, scale);
    ctx2.rotate(Math.sin(x) * 0.2);
    ctx2.lineWidth = 2;
    ctx2.strokeStyle = "#45294d";
    const colors = CANDY_SCENES[level2 - 1], shape = (level2 - 1) % 7;
    if (level2 > 1) {
      ctx2.fillStyle = colors[2 + kind % 2];
      ctx2.shadowColor = colors[2];
      ctx2.shadowBlur = level2 >= 7 ? 12 : 3;
      ctx2.beginPath();
      if (shape === 1) {
        for (let i = 0; i < 5; i++) {
          const a = i * Math.PI * 2 / 5;
          ctx2.moveTo(Math.cos(a) * 12 + 13, Math.sin(a) * 11);
          ctx2.arc(Math.cos(a) * 12, Math.sin(a) * 11, 13, 0, Math.PI * 2);
        }
      } else if (shape === 2) {
        ctx2.roundRect(-17, -15, 34, 39, 12);
        ctx2.moveTo(-6, -17);
        ctx2.arc(-13, -18, 9, 0, Math.PI * 2);
        ctx2.moveTo(22, -17);
        ctx2.arc(13, -18, 9, 0, Math.PI * 2);
      } else if (shape === 3) {
        ctx2.arc(0, 0, 24, 0, Math.PI * 2);
      } else if (shape === 4) {
        for (let i = 0; i < 6; i++) {
          const a = i * Math.PI / 3;
          ctx2.lineTo(Math.cos(a) * 25, Math.sin(a) * 25);
        }
        ctx2.closePath();
      } else if (shape === 5) {
        ctx2.roundRect(-11, -28, 22, 56, 10);
      } else if (shape === 6) {
        ctx2.roundRect(-24, -18, 48, 36, 7);
      } else {
        ctx2.ellipse(0, 0, 23, 19, 0.4, 0, Math.PI * 2);
      }
      ctx2.fill();
      ctx2.stroke();
      ctx2.shadowBlur = 0;
      ctx2.save();
      ctx2.clip();
      ctx2.strokeStyle = colors[3 - kind % 2];
      ctx2.lineWidth = 7;
      if (shape === 3) {
        for (let i = 0; i < 6; i++) {
          ctx2.beginPath();
          ctx2.moveTo(0, 0);
          ctx2.lineTo(Math.cos(i * Math.PI / 3) * 26, Math.sin(i * Math.PI / 3) * 26);
          ctx2.stroke();
        }
      } else {
        for (let i = -40; i < 45; i += 15) {
          ctx2.beginPath();
          ctx2.moveTo(i, -32);
          ctx2.lineTo(i + 28, 32);
          ctx2.stroke();
        }
      }
      ctx2.restore();
      ctx2.fillStyle = "#ffffff88";
      ctx2.beginPath();
      ctx2.ellipse(-8, -10, 4, 8, 0.7, 0, Math.PI * 2);
      ctx2.fill();
      if (level2 === 10) {
        ctx2.fillStyle = "#ffd25c";
        ctx2.beginPath();
        ctx2.moveTo(-19, -20);
        ctx2.lineTo(-23, -37);
        ctx2.lineTo(-7, -29);
        ctx2.lineTo(0, -43);
        ctx2.lineTo(8, -29);
        ctx2.lineTo(23, -37);
        ctx2.lineTo(19, -20);
        ctx2.closePath();
        ctx2.fill();
        ctx2.stroke();
      }
      ctx2.restore();
      return;
    }
    if (kind === 0) {
      ctx2.rotate(-0.45);
      ctx2.beginPath();
      ctx2.moveTo(-10, -21);
      ctx2.bezierCurveTo(23, -27, 25, 23, 2, 23);
      ctx2.bezierCurveTo(-24, 24, -10, 6, -19, -3);
      ctx2.bezierCurveTo(-24, -12, -20, -19, -10, -21);
      ctx2.fillStyle = "#ef82b9";
      ctx2.fill();
      ctx2.stroke();
      ctx2.fillStyle = "#ffd7ed";
      ctx2.beginPath();
      ctx2.ellipse(-6, -10, 4, 8, 0.2, 0, Math.PI * 2);
      ctx2.fill();
    }
    if (kind === 1) {
      ctx2.fillStyle = "#ffb74c";
      ctx2.beginPath();
      ctx2.moveTo(-14, -8);
      ctx2.lineTo(-30, -17);
      ctx2.lineTo(-30, 16);
      ctx2.lineTo(-14, 8);
      ctx2.moveTo(14, -8);
      ctx2.lineTo(30, -17);
      ctx2.lineTo(30, 16);
      ctx2.lineTo(14, 8);
      ctx2.fill();
      ctx2.stroke();
      ctx2.fillStyle = "#ffe18d";
      ctx2.beginPath();
      ctx2.roundRect(-18, -15, 36, 30, 10);
      ctx2.fill();
      ctx2.stroke();
      ctx2.strokeStyle = "#ee8150";
      ctx2.lineWidth = 5;
      ctx2.beginPath();
      ctx2.moveTo(-8, -12);
      ctx2.lineTo(7, 12);
      ctx2.stroke();
    }
    if (kind === 2) {
      ctx2.fillStyle = "#9be5d9";
      ctx2.beginPath();
      ctx2.arc(0, 0, 23, 0, Math.PI * 2);
      ctx2.fill();
      ctx2.stroke();
      ctx2.strokeStyle = "#4baeb6";
      ctx2.lineWidth = 4;
      ctx2.beginPath();
      for (let i = 0; i < 70; i++) {
        const a = i * 0.16, r = i * 0.28;
        ctx2.lineTo(Math.cos(a) * r, Math.sin(a) * r);
      }
      ctx2.stroke();
    }
    ctx2.restore();
  }
  function drawCandy(ctx2, s, art2, reduced = false) {
    const palette = scene(s), g = ctx2.createLinearGradient(0, 0, 720, 720);
    g.addColorStop(0, palette[0]);
    g.addColorStop(1, palette[1]);
    ctx2.fillStyle = g;
    ctx2.fillRect(0, 0, 720, 720);
    ctx2.save();
    ctx2.globalAlpha = 0.12;
    ctx2.strokeStyle = palette[2];
    ctx2.lineWidth = 2;
    for (let i = 0; i < 8; i++) {
      const x = (i * 173 + 90) % 720, y = (i * 119 + (reduced ? 0 : s.elapsed * (5 + s.level))) % 720;
      ctx2.beginPath();
      if (s.level % 3 === 0) {
        ctx2.ellipse(x, y, 85, 28, -0.4, 0, Math.PI * 2);
      } else if (s.level % 3 === 1) {
        ctx2.moveTo(x - 60, y - 40);
        ctx2.lineTo(x + 60, y + 40);
        ctx2.moveTo(x + 60, y - 40);
        ctx2.lineTo(x - 60, y + 40);
      } else {
        ctx2.arc(x, y, 35 + i * 3, 0, Math.PI * 2);
      }
      ctx2.stroke();
    }
    ctx2.restore();
    ctx2.save();
    ctx2.strokeStyle = "#ffffff09";
    ctx2.lineWidth = 1;
    for (let x = 0; x < 720; x += 48) {
      ctx2.beginPath();
      ctx2.moveTo(x, 0);
      ctx2.lineTo(x, 720);
      ctx2.stroke();
    }
    for (let y = 0; y < 720; y += 48) {
      ctx2.beginPath();
      ctx2.moveTo(0, y);
      ctx2.lineTo(720, y);
      ctx2.stroke();
    }
    for (let i = 0; i < 44; i++) {
      ctx2.fillStyle = i % 3 ? "#c5edf177" : "#ffd88caa";
      const y = (i * 137 + (reduced ? 0 : s.elapsed * 16)) % 720;
      ctx2.fillRect(i * 191 % 710, y, i % 4 === 0 ? 3 : 2, 2);
    }
    ctx2.restore();
    ctx2.fillStyle = "#ffffff07";
    ctx2.fillRect(0, 350, 720, 370);
    ctx2.strokeStyle = "#b6e9d425";
    ctx2.setLineDash([5, 9]);
    ctx2.beginPath();
    ctx2.moveTo(15, 350);
    ctx2.lineTo(705, 350);
    ctx2.stroke();
    ctx2.setLineDash([]);
    for (const e of s.enemies) {
      if (e.warning) {
        ctx2.strokeStyle = "#ffdc8e";
        ctx2.lineWidth = 2;
        ctx2.setLineDash([5, 8]);
        ctx2.beginPath();
        ctx2.moveTo(e.x, e.y + 30);
        ctx2.lineTo(e.x, 660);
        ctx2.stroke();
        ctx2.setLineDash([]);
        ctx2.fillStyle = "#ffe3a6";
        ctx2.font = "bold 12px Arial";
        ctx2.textAlign = "center";
        ctx2.fillText("DIVE!", e.x, e.y - 30);
      }
      candy(ctx2, e.x, e.y, e.kind, e.boss ? 1.8 : 1, s.level);
      if (e.hp > 1) {
        ctx2.fillStyle = "#fff5df";
        ctx2.font = "bold 12px Arial";
        ctx2.textAlign = "center";
        ctx2.fillText(e.boss ? "BOSS \xB7 PHASE " + (e.phase || 1) : "\u2022".repeat(Math.min(3, e.hp)), e.x, e.y - 30 * (e.boss ? 1.6 : 1));
      }
      if (e.boss) {
        ctx2.fillStyle = "#fff4";
        ctx2.fillRect(e.x - 48, e.y + 48, 96, 5);
        ctx2.fillStyle = palette[2];
        ctx2.fillRect(e.x - 48, e.y + 48, 96 * e.hp / e.maxHp, 5);
      }
    }
    for (const p2 of s.shots) {
      ctx2.strokeStyle = "#ffce72";
      ctx2.lineWidth = 5;
      ctx2.lineCap = "round";
      ctx2.beginPath();
      ctx2.moveTo(p2.x, p2.y + 13);
      ctx2.lineTo(p2.x, p2.y - 7);
      ctx2.stroke();
      ctx2.strokeStyle = "#fff5d2";
      ctx2.lineWidth = 2;
      ctx2.stroke();
    }
    for (const b of s.bullets) {
      ctx2.save();
      ctx2.translate(b.x, b.y);
      ctx2.rotate(0.4);
      ctx2.fillStyle = "#fda1d9";
      ctx2.fillRect(-3, -7, 6, 14);
      ctx2.restore();
    }
    for (const d of s.drops) {
      ctx2.fillStyle = d.kind === "shield" ? "#b0f4e1" : "#ffde81";
      ctx2.beginPath();
      ctx2.arc(d.x, d.y, 18, 0, Math.PI * 2);
      ctx2.fill();
      ctx2.fillStyle = "#19363e";
      ctx2.textAlign = "center";
      ctx2.font = "bold 17px Arial";
      ctx2.fillText(d.kind === "shield" ? "S" : "3", d.x, d.y + 6);
    }
    const p = s.player;
    ctx2.save();
    ctx2.translate(p.x, p.y);
    if (s.invincible > 0 && Math.floor(s.elapsed * 8) % 2) ctx2.globalAlpha = 0.6;
    ctx2.fillStyle = "#6be8e599";
    ctx2.beginPath();
    ctx2.moveTo(-17, 30);
    ctx2.lineTo(0, 60 + (reduced ? 0 : Math.sin(s.elapsed * 25) * 8));
    ctx2.lineTo(17, 30);
    ctx2.fill();
    ctx2.fillStyle = "#fff5be";
    ctx2.beginPath();
    ctx2.moveTo(-8, 32);
    ctx2.lineTo(0, 49);
    ctx2.lineTo(8, 32);
    ctx2.fill();
    if (s.shield > 0) {
      ctx2.strokeStyle = "#a9ffdb";
      ctx2.lineWidth = 3;
      ctx2.beginPath();
      ctx2.ellipse(0, 0, 46, 55, 0, 0, Math.PI * 2);
      ctx2.stroke();
    }
    const img = art2[(s.level - 1) % 5];
    if (img?.complete && img.naturalWidth) ctx2.drawImage(img, -26, -38, 52, 76);
    else {
      ctx2.fillStyle = "#4ec1c3";
      ctx2.fillRect(-24, -35, 48, 70);
    }
    ctx2.restore();
    for (const p2 of s.sparks) {
      ctx2.globalAlpha = Math.max(0, p2.life * 2);
      ctx2.fillStyle = p2.color;
      ctx2.fillRect(p2.x, p2.y, 5, 5);
    }
    ctx2.globalAlpha = 1;
    ctx2.textAlign = "left";
    ctx2.fillStyle = "#d9efe4";
    ctx2.font = "bold 12px Arial";
    ctx2.fillText("JOBURG / CANDY DEFENDER", 24, 28);
    ctx2.textAlign = "right";
    ctx2.fillStyle = "#ffdd88";
    ctx2.fillText(`WAVE ${s.wave} / ${s.waves}`, 696, 28);
    if (s.banner > 0) {
      ctx2.fillStyle = "#12222ce8";
      ctx2.beginPath();
      ctx2.roundRect(70, 288, 580, 105, 16);
      ctx2.fill();
      ctx2.textAlign = "center";
      ctx2.fillStyle = "#fff3d8";
      ctx2.font = "bold 23px Arial";
      ctx2.fillText(`WAVE ${s.wave} \xB7 LET\u2019S CRUNCH`, 360, 326);
      ctx2.font = "15px Arial";
      ctx2.fillText(palette[5], 360, 359);
    }
  }

  // apps/arcade/arcade.mjs
  var $ = (s) => document.querySelector(s);
  var $$ = (s) => [...document.querySelectorAll(s)];
  var STORE = "joburg-arcade-review-v1";
  var ROUND = "joburg-arcade-round-v1";
  var profile = emptyProfile();
  var storageOK = true;
  try {
    const raw = localStorage.getItem(STORE);
    if (raw) profile = validateProfile(JSON.parse(raw));
  } catch {
    storageOK = false;
  }
  var game = "run";
  var level = 1;
  var mode = "lobby";
  var run = null;
  var flip = null;
  var overlayAction = null;
  var last = 0;
  var moveClock = 0;
  var enemyClock = 0;
  var saveClock = 0;
  var desired = null;
  var direction = null;
  var held = null;
  var touchStart = null;
  var candy2 = null;
  var candyTarget = null;
  var keys = /* @__PURE__ */ new Set();
  var canvas = $("#maze");
  var ctx = canvas.getContext("2d");
  var art = FLAVORS.map((f, n) => {
    const i = new Image();
    i.src = window.JoburgArcadeConfig?.art?.[n] || `/art/${f}.webp`;
    return i;
  });
  var brandLogo = new Image();
  brandLogo.src = window.JoburgArcadeConfig?.logo || "/art/logo.png";
  document.querySelectorAll("[data-shop-box]").forEach((a) => a.href = window.JoburgArcadeConfig?.box || "https://joburgmeats.com/pages/biltong?view=studio-builder#bundle-builder");
  document.querySelectorAll("[data-shop-art]").forEach((img) => img.src = art[+img.dataset.shopArt].src);
  var shopShortcut = document.createElement("a");
  shopShortcut.className = "shop-shortcut";
  shopShortcut.textContent = "Build your box \u2197";
  shopShortcut.href = window.JoburgArcadeConfig?.box || "https://joburgmeats.com/pages/biltong?view=studio-builder#bundle-builder";
  document.querySelector("header").insertBefore(shopShortcut, $("#profileButton"));
  $("#powerGuide .muted").textContent += " Look for the big steak: 10 seconds of monster-eating power. Occasional Joburg-logo tokens give +100 game points. Bonus treats disappear after their timer; they are optional.";
  var visual = { player: null, enemies: [], pops: [], reduced: matchMedia("(prefers-reduced-motion: reduce)").matches, direction: null };
  var playerTween = null;
  var enemyTweens = [];
  function snapVisual() {
    visual.player = [...run.player];
    visual.enemies = run.enemies.map((p) => [...p]);
    visual.pops = [];
    playerTween = null;
    enemyTweens = [];
  }
  function tween(from, to, duration) {
    return { from: [...from], to: [...to], duration, age: 0 };
  }
  function advanceVisual(dt) {
    const advance = (t) => {
      t.age = Math.min(t.duration, t.age + dt);
      const u = t.age / t.duration;
      return t.from.map((v, i) => v + (t.to[i] - v) * u);
    };
    if (playerTween) visual.player = advance(playerTween);
    enemyTweens.forEach((t, i) => {
      if (t) visual.enemies[i] = advance(t);
    });
    visual.pops = visual.pops.filter((p) => (p.age += dt) < p.life);
    visual.direction = direction;
  }
  function pop(text, at, color = "#ffec9e") {
    const nearby = visual.pops.filter((p) => Math.abs(p.x - at[0]) < 1 && Math.abs(p.y - at[1]) < 2).length;
    visual.pops.push({ text, x: at[0], y: at[1] - nearby * 0.45, color, age: 0, life: 1.15 });
  }
  function persist() {
    try {
      localStorage.setItem(STORE, JSON.stringify(profile));
      storageOK = true;
    } catch {
      storageOK = false;
    }
    refreshLobby();
  }
  function saveRound() {
    if (!["ready", "playing", "paused"].includes(mode) || !run && !flip && !candy2) return;
    try {
      localStorage.setItem(ROUND, JSON.stringify({ game, level, run, flip, candy: candy2 }));
    } catch {
      storageOK = false;
    }
  }
  function clearRound() {
    try {
      localStorage.removeItem(ROUND);
    } catch {
    }
  }
  function savedRound() {
    try {
      const r = JSON.parse(localStorage.getItem(ROUND) || "null");
      if (!r || !LIMITS[r.game] || !Number.isInteger(r.level) || r.level < 1 || r.level > profile[r.game].unlocked) return null;
      if (r.game === "run") {
        const fresh = maze(r.level), s = r.run;
        if (!s || !Array.isArray(s.player) || fresh.grid[s.player[1]]?.[s.player[0]] !== 0 || !Array.isArray(s.pellets) || s.pellets.some((p) => !fresh.pellets.includes(p)) || !Array.isArray(s.power) || s.power.some((p) => !fresh.power.some((f) => f.p === p.p && f.flavor === p.flavor)) || !Array.isArray(s.enemies) || s.enemies.length < 1 || s.enemies.length > 4 || s.enemies.some((p) => !Array.isArray(p) || fresh.grid[p[1]]?.[p[0]] !== 0) || !Number.isFinite(s.score) || s.score < 0 || !Number.isFinite(s.elapsed) || !Number.isInteger(s.lives) || s.lives < 1 || s.lives > 3) return null;
        s.grid = fresh.grid;
        s.invincible = 2;
        if (s.enemies.length !== fresh.enemies.length) s.enemies = fresh.enemies;
        s.enemySleep = s.enemies.map((_, i) => Number.isFinite(s.enemySleep?.[i]) ? Math.max(0, Math.min(4, s.enemySleep[i])) : 0);
        s.effect = Number.isInteger(s.effect) && s.effect >= 0 && s.effect < 5 ? s.effect : null;
        s.powerTime = s.effect !== null && Number.isFinite(s.powerTime) ? Math.max(0, Math.min(10, s.powerTime)) : 0;
        s.eatCombo = Number.isInteger(s.eatCombo) ? Math.max(0, Math.min(4, s.eatCombo)) : 0;
      } else if (r.game === "candy") {
        r.candy = restoreCandy(r.candy, r.level);
        if (!r.candy) return null;
      } else {
        const s = r.flip, c = flipLevel(r.level).cards;
        if (!s || JSON.stringify(s.cards) !== JSON.stringify(c) || !Array.isArray(s.matched) || new Set(s.matched).size !== s.matched.length || s.matched.some((i) => !Number.isInteger(i) || i < 0 || i >= c.length) || !Number.isInteger(s.moves) || s.moves < 0) return null;
        s.open = [];
        s.wait = 0;
      }
      return r;
    } catch {
      return null;
    }
  }
  function refreshLobby() {
    $("#playerName").textContent = profile.name;
    let stars = 0, levels = 0;
    for (const g of Object.keys(LIMITS)) {
      const values = Object.values(profile[g].stars);
      stars += values.reduce((a, b) => a + b, 0);
      levels += values.length;
      $(`#${g}Progress`).textContent = `LEVEL ${profile[g].unlocked} UNLOCKED`;
    }
    $("#totalStars").textContent = stars;
    $("#levelsDone").textContent = levels;
    $("#saveStatus").textContent = storageOK ? "Progress saves on this browser. Back it up in Your progress." : "Browser saving unavailable. Use a progress backup before leaving.";
  }
  function map() {
    $("#levelMap").replaceChildren();
    for (let i = 1; i <= LIMITS[game]; i++) {
      const b = document.createElement("button");
      b.disabled = i > profile[game].unlocked;
      b.classList.toggle("current", i === level);
      b.textContent = b.disabled ? "\u{1F512}" : i;
      const s = document.createElement("small");
      s.textContent = "\u2605".repeat(profile[game].stars[i] || 0) || "\u2014";
      b.append(s);
      b.setAttribute("aria-label", `Level ${i}${b.disabled ? ", locked" : `, ${profile[game].stars[i] || 0} stars`}`);
      b.onclick = () => {
        if (mode === "playing" && !confirm("Start another level? Your current round will be replaced.")) return;
        startLevel(game, i);
      };
      $("#levelMap").append(b);
    }
    $("#personalBest").textContent = `Personal best: ${profile[game].best.toLocaleString()} points`;
  }
  function showOverlay(kicker, title, copy, label, action) {
    $("#overlayKicker").textContent = kicker;
    $("#overlayTitle").textContent = title;
    $("#overlayCopy").textContent = copy;
    $("#overlayAction").textContent = label;
    overlayAction = action;
    $("#overlay").hidden = false;
    $("#overlayAction").focus({ preventScroll: true });
  }
  $("#overlayAction").onclick = () => overlayAction?.();
  function setupView() {
    $("#lobby").hidden = true;
    $("#play").hidden = false;
    $("#gameLabel").textContent = game === "run" ? "SNACK RUN / MAZE ADVENTURE" : "FLAVOR FLIP / MEMORY & MASTERY";
    $("#maze").hidden = game !== "run";
    $("#memory").hidden = game !== "flip";
    $("#dpad").hidden = game !== "run";
    $("#powerGuide").hidden = game !== "run";
    $("#worldTitle").textContent = game === "run" ? WORLDS[Math.floor((level - 1) / 2)] : ["Meet your flavors", "Find your rhythm", "Make it a streak", "Sharp instincts", "Double the flavor", "Memory matters", "The flavor archive", "Master of the match"][level - 1];
    $("#instructions").textContent = game === "run" ? "Collect every steak slice to clear the maze. Use arrows, WASD, swipe or the direction pad. You have three hearts. More monsters join as you advance." : "Turn over two cards to find a pair. Match the flavor AND its numbered badge. Clear within the move target for three stars; you can always keep playing to finish.";
    $("#level").textContent = level;
    $("#pause").textContent = "Pause";
    $("#powerStatus").textContent = "";
    map();
  }
  function startLevel(g, l, restore = null) {
    game = g;
    level = l;
    mode = "ready";
    run = null;
    flip = null;
    candy2 = null;
    direction = desired = null;
    candyTarget = null;
    keys.clear();
    moveClock = enemyClock = 0;
    setupView();
    if (game === "candy") {
      candy2 = restore?.candy || newCandy(level);
      $("#maze").hidden = false;
      $("#dpad").hidden = false;
      $("#gameLabel").textContent = "CANDY DEFENDER / FLAVOR IN FLIGHT";
      $("#worldTitle").textContent = CANDY_WORLDS[level - 1];
      $("#instructions").textContent = CANDY_SCENES[level - 1][5] + " Arrows or WASD to fly; drag the pouch area on touch screens. Your pouch fires automatically. Catch a 3 for triple shots or S for an eight-second shield. Watch the gold DIVE warning. Bosses on levels 3, 6, 9 and 10 change attacks at half health. Game scores have no cash or discount value.";
      canvas.setAttribute("aria-label", "Candy Defender. Use arrows or WASD to fly, drag to steer on touch screens. Automatic firing. P to pause.");
      drawCandy(ctx, candy2, art, visual.reduced);
      stats();
      saveRound();
      showOverlay(restore ? "WELCOME BACK" : `LEVEL ${level}`, CANDY_WORLDS[level - 1], `${candy2.waves} candy waves. Three hearts. Dodge, collect power-ups and defend your flavor.`, restore ? "Continue flying \u2192" : "Launch my pouch \u2192", resume);
      return;
    }
    canvas.setAttribute("aria-label", "Snack Run maze. Use arrows or WASD to move; P to pause. Collect slices and power pouches, avoid monsters.");
    if (game === "run") $("#instructions").textContent = "Collect every steak slice. Every pouch makes monsters edible: chase the pale-blue monsters before your power runs out! Each flavor also adds its own bonus. Use arrows, WASD, swipe or the direction pad.";
    else $("#instructions").textContent += " Match pairs and beat your personal best.";
    if (game === "run") {
      run = restore?.run || { ...maze(level), player: [1, 1], lives: 3, score: 0, elapsed: 0, effect: null, powerTime: 0, invincible: 3, eatCombo: 0 };
      run.enemySleep ??= run.enemies.map(() => 0);
      initMazeTreats(run);
      snapVisual();
      drawMaze();
    } else {
      flip = restore?.flip || { ...flipLevel(level), open: [], matched: [], moves: 0, score: 0, streak: 0, elapsed: 0, wait: 0 };
      renderFlip();
    }
    stats();
    saveRound();
    showOverlay(restore ? "WELCOME BACK" : `LEVEL ${level}`, restore ? "Your adventure is waiting." : $("#worldTitle").textContent, game === "run" ? `${run.pellets.length} slices. ${run.enemies.length} hunger monster${run.enemies.length > 1 ? "s" : ""}. Five pouches with different powers.` : `Find ${flip.pairs} pairs. Aim for ${flip.target} moves or fewer. No countdown.`, restore ? "Continue this round \u2192" : "Let\u2019s play \u2192", resume);
  }
  function resume() {
    mode = "playing";
    $("#overlay").hidden = true;
    $("#pause").textContent = "Pause";
    last = performance.now();
    if (game !== "flip") canvas.focus({ preventScroll: true });
    else $("#memory button:not(:disabled)")?.focus({ preventScroll: true });
  }
  function pause() {
    if (mode !== "playing") return;
    mode = "paused";
    held = null;
    keys.clear();
    candyTarget = null;
    saveRound();
    $("#pause").textContent = "Resume";
    showOverlay("TAKE YOUR TIME", "A little snack break.", "Your round is saved. Pick up right here when you\u2019re ready.", "Back to it \u2192", resume);
  }
  $("#pause").onclick = () => mode === "paused" ? resume() : pause();
  $("#back").onclick = () => {
    if (mode === "playing" || mode === "paused" || mode === "ready") saveRound();
    mode = "lobby";
    held = null;
    $("#play").hidden = true;
    $("#lobby").hidden = false;
    refreshLobby();
    $("[data-open]").focus({ preventScroll: true });
  };
  $("#restart").onclick = () => {
    if (confirm("Restart this level? Your unlocked levels and stars will stay saved.")) startLevel(game, level);
  };
  $$("[data-open]").forEach((b) => b.onclick = () => {
    const g = b.dataset.open, r = savedRound();
    startLevel(g, r?.game === g ? r.level : profile[g].unlocked, r?.game === g ? r : null);
    $("#play").scrollIntoView({ block: "start" });
  });
  function stats() {
    const s = game === "run" ? run : game === "candy" ? candy2 : flip;
    if (!s) return;
    $("#score").textContent = s.score;
    $("#thirdStat").textContent = game !== "flip" ? `${"\u2665".repeat(s.lives)}${"\u2661".repeat(3 - s.lives)}` : `${flip.moves} / ${flip.target} moves`;
    $("#liveHint").textContent = game === "candy" ? `Wave ${s.wave}/${s.waves} \xB7 ${s.enemies.length} candies left` : game === "run" ? `${run.pellets.length} slices left \xB7 ${Math.floor(run.elapsed)}s` : `${flip.matched.length / 2} / ${flip.pairs} pairs \xB7 ${flip.streak > 1 ? flip.streak + "\xD7 match streak" : "Find your next pair"}`;
  }
  function win() {
    mode = "won";
    const stars = game === "candy" ? candy2.lives : game === "run" ? run.lives === 3 ? 3 : run.lives === 2 ? 2 : 1 : flip.moves <= flip.target ? 3 : flip.moves <= flip.target + 4 ? 2 : 1;
    const score = (game === "candy" ? candy2 : game === "run" ? run : flip).score;
    complete(profile, game, level, stars, score);
    persist();
    clearRound();
    map();
    const preview = " Game scores have no cash or discount value.";
    showOverlay("\u2605".repeat(stars), level === LIMITS[game] ? "You mastered every level!" : "That\u2019s a tasty victory.", `${score.toLocaleString()} game points. ${stars} star${stars > 1 ? "s" : ""} saved. ${level < LIMITS[game] ? "The next level is now unlocked." : "Replay any level to improve your stars."}${preview}`, level < LIMITS[game] ? "Next level \u2192" : "Replay the finale \u2192", () => startLevel(game, Math.min(LIMITS[game], level + 1)));
  }
  function lose() {
    mode = "lost";
    clearRound();
    showOverlay("THE MONSTERS GOT HUNGRY", "You\u2019ve got another run in you.", "Learn their routes, grab a power pouch and try again. Your previously unlocked levels are safe.", "Try this level again \u2192", () => startLevel(game, level));
  }
  function passable(p) {
    return run.grid[p[1]]?.[p[0]] === 0;
  }
  function move() {
    const from = [...visual.player || run.player];
    const tryMove = (d) => {
      if (d === null) return false;
      const p = [run.player[0] + DIRS[d][0], run.player[1] + DIRS[d][1]];
      if (!passable(p)) return false;
      run.player = p;
      direction = d;
      return true;
    };
    if (!tryMove(desired)) tryMove(direction);
    playerTween = tween(from, run.player, mazePace(level, run.effect === 2).player);
    const at = key(...run.player), i = run.pellets.indexOf(at);
    if (i >= 0) {
      run.pellets.splice(i, 1);
      run.score += run.effect === 4 ? 20 : 10;
    }
    const pi = run.power.findIndex((p) => p.p === at);
    if (pi >= 0) {
      const p = run.power.splice(pi, 1)[0];
      pouchPower(run, p.flavor);
      pop("POWER UP!", run.player);
    }
    const treat = collectMazeTreat(run);
    if (treat) pop(treat === "logo" ? "+100 JOBURG!" : "STEAK POWER!", run.player);
    collision();
    if (mode === "playing" && !run.pellets.length) win();
    stats();
  }
  function collision() {
    const contact = monsterContact(run);
    for (const e of contact.eaten) {
      pop(`+${e.points}`, run.enemies[e.index]);
      run.enemies[e.index] = maze(level).enemies[e.index];
      visual.enemies[e.index] = [...run.enemies[e.index]];
      enemyTweens[e.index] = null;
    }
    if (contact.eaten.length) stats();
    if (!contact.hit) return;
    run.lives--;
    if (run.lives === 0) {
      stats();
      lose();
      return;
    }
    run.player = [1, 1];
    run.enemies = maze(level).enemies;
    run.enemySleep = run.enemies.map(() => 0);
    run.invincible = 3;
    direction = desired = null;
    run.effect = null;
    run.powerTime = 0;
    snapVisual();
    stats();
  }
  function monsters() {
    if (run.effect === 1 && run.powerTime > 0) return;
    run.enemies = run.enemies.map((p, i) => {
      if (run.enemySleep[i] > 0) return p;
      const target = i === 0 || Math.floor(run.elapsed + i * 2) % 8 < 5 ? run.player : [run.grid.length - 2, 1];
      const next = run.powerTime > 0 ? fleeStep(run.grid, p, run.player, i) : nextStep(run.grid, p, target);
      enemyTweens[i] = tween(visual.enemies[i] || p, next, mazePace(level).monster);
      return next;
    });
    collision();
  }
  function drawMaze() {
    if (run) renderMaze(ctx, run, art, visual, brandLogo);
  }
  function renderFlip(focusIndex = null) {
    const area = $("#memory");
    area.replaceChildren();
    flip.cards.forEach((id, i) => {
      const b = document.createElement("button"), shown = flip.open.includes(i) || flip.matched.includes(i), matched = flip.matched.includes(i);
      b.classList.toggle("revealed", shown);
      b.classList.toggle("matched", matched);
      b.disabled = matched;
      b.setAttribute("aria-label", matched ? `Card ${i + 1}, matched ${FLAVORS[id % 5]}, pair ${id + 1}` : shown ? `${FLAVORS[id % 5]}, pair ${id + 1}` : `Reveal card ${i + 1}`);
      if (shown) {
        const img = document.createElement("img");
        img.src = art[id % 5].src;
        img.alt = "";
        const badge = document.createElement("span");
        badge.className = "pair-label";
        badge.textContent = `${matched ? "\u2713 " : ""}${id + 1}`;
        b.append(img, badge);
      } else b.textContent = "\u2733";
      b.onclick = () => choose(i);
      area.append(b);
    });
    if (focusIndex !== null) area.children[focusIndex]?.focus({ preventScroll: true });
  }
  function choose(i) {
    if (mode !== "playing" || flip.wait > 0 || flip.open.includes(i) || flip.matched.includes(i)) return;
    flip.open.push(i);
    if (flip.open.length === 2) {
      flip.moves++;
      const [a, b] = flip.open;
      if (flip.cards[a] === flip.cards[b]) {
        flip.streak++;
        flip.score += 100 + Math.min(5, flip.streak - 1) * 25;
        flip.matched.push(a, b);
        flip.open = [];
        renderFlip();
        stats();
        saveRound();
        if (flip.matched.length === flip.cards.length) win();
        else $("#memory button:not(:disabled)")?.focus({ preventScroll: true });
        return;
      }
      flip.streak = 0;
      flip.wait = 1.05;
    }
    renderFlip(i);
    stats();
    saveRound();
  }
  function setDir(d) {
    if (mode !== "playing" || game !== "run") return;
    desired = d;
  }
  window.addEventListener("keydown", (e) => {
    if (game !== "candy" || mode !== "playing" || $("#profileDialog").open) return;
    const dirs = { ArrowUp: 0, w: 0, W: 0, ArrowRight: 1, d: 1, D: 1, ArrowDown: 2, s: 2, S: 2, ArrowLeft: 3, a: 3, A: 3 };
    if (dirs[e.key] !== void 0) {
      e.preventDefault();
      keys.add(dirs[e.key]);
      candyTarget = null;
    }
  });
  window.addEventListener("keyup", (e) => {
    const dirs = { ArrowUp: 0, w: 0, W: 0, ArrowRight: 1, d: 1, D: 1, ArrowDown: 2, s: 2, S: 2, ArrowLeft: 3, a: 3, A: 3 };
    keys.delete(dirs[e.key]);
  });
  window.addEventListener("blur", () => {
    keys.clear();
    held = null;
    pause();
  });
  function candyPointer(e) {
    if (game !== "candy" || mode !== "playing") return;
    const r = canvas.getBoundingClientRect();
    candyTarget = { x: (e.clientX - r.left) / r.width * 720, y: (e.clientY - r.top) / r.height * 720 };
  }
  canvas.addEventListener("pointerdown", candyPointer);
  canvas.addEventListener("pointermove", (e) => {
    if (touchStart) candyPointer(e);
  });
  canvas.addEventListener("pointerup", () => {
    candyTarget = null;
  });
  canvas.addEventListener("pointercancel", () => {
    candyTarget = null;
  });
  window.addEventListener("keydown", (e) => {
    if ($("#profileDialog").open || mode === "lobby" || /INPUT|TEXTAREA/.test(e.target.tagName)) return;
    const dirs = { ArrowUp: 0, w: 0, W: 0, ArrowRight: 1, d: 1, D: 1, ArrowDown: 2, s: 2, S: 2, ArrowLeft: 3, a: 3, A: 3 };
    if (dirs[e.key] !== void 0 && game === "run") {
      e.preventDefault();
      setDir(dirs[e.key]);
    }
    if (e.key.toLowerCase() === "p") {
      e.preventDefault();
      mode === "paused" ? resume() : pause();
    }
  });
  $$("[data-dir]").forEach((b) => {
    b.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      held = +b.dataset.dir;
      setDir(held);
      b.setPointerCapture(e.pointerId);
    });
    b.addEventListener("pointerup", () => held = null);
    b.addEventListener("pointercancel", () => held = null);
    b.addEventListener("click", () => setDir(+b.dataset.dir));
  });
  canvas.addEventListener("pointerdown", (e) => {
    touchStart = [e.clientX, e.clientY];
    canvas.setPointerCapture(e.pointerId);
  });
  canvas.addEventListener("pointermove", (e) => {
    if (!touchStart) return;
    const dx = e.clientX - touchStart[0], dy = e.clientY - touchStart[1];
    if (Math.max(Math.abs(dx), Math.abs(dy)) < 16) return;
    setDir(Math.abs(dx) > Math.abs(dy) ? dx > 0 ? 1 : 3 : dy > 0 ? 2 : 0);
    touchStart = [e.clientX, e.clientY];
  });
  canvas.addEventListener("pointerup", () => touchStart = null);
  canvas.addEventListener("pointercancel", () => touchStart = null);
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) pause();
  });
  window.addEventListener("pagehide", () => {
    if (["playing", "paused", "ready"].includes(mode)) saveRound();
  });
  function tick(now) {
    const dt = Math.min(0.06, (now - last) / 1e3 || 0);
    last = now;
    if (mode === "playing") {
      if (game === "run") {
        advanceVisual(dt);
        run.elapsed += dt;
        updateMazeTreats(run);
        run.invincible = Math.max(0, run.invincible - dt);
        run.powerTime = Math.max(0, run.powerTime - dt);
        run.enemySleep = run.enemySleep.map((t) => Math.max(0, t - dt));
        if (run.powerTime === 0) {
          run.effect = null;
          run.eatCombo = 0;
        }
        moveClock += dt;
        enemyClock += dt;
        if (held !== null) desired = held;
        const pace = mazePace(level, run.effect === 2);
        if (moveClock >= pace.player) {
          moveClock -= pace.player;
          move();
        }
        if (mode === "playing" && enemyClock >= pace.monster) {
          enemyClock -= pace.monster;
          monsters();
        }
        $("#powerStatus").textContent = run.powerTime > 0 ? `EAT MONSTERS \xB7 ${Math.ceil(run.powerTime)}s` : "";
        drawMaze();
      } else if (game === "candy") {
        const active = (d) => keys.has(d) || held === d;
        const status = stepCandy(candy2, dt, { x: Number(active(1)) - Number(active(3)), y: Number(active(2)) - Number(active(0)), target: candyTarget });
        drawCandy(ctx, candy2, art, visual.reduced);
        stats();
        $("#powerStatus").textContent = [candy2.rapid > 0 ? `TRIPLE ${Math.ceil(candy2.rapid)}s` : "", candy2.shield > 0 ? `SHIELD ${Math.ceil(candy2.shield)}s` : ""].filter(Boolean).join(" \xB7 ");
        if (status === "won") win();
        else if (status === "lost") {
          mode = "lost";
          clearRound();
          showOverlay("THAT WAS A SUGAR RUSH", "Ready for another flight?", "Dodge the pink sprinkles and catch power-ups. Your unlocked levels are safe.", "Fly again \u2192", () => startLevel(game, level));
        }
      } else {
        flip.elapsed += dt;
        if (flip.wait > 0) {
          flip.wait = Math.max(0, flip.wait - dt);
          if (flip.wait === 0) {
            flip.open = [];
            renderFlip();
          }
        }
      }
      saveClock += dt;
      if (saveClock > 1) {
        saveClock = 0;
        saveRound();
        stats();
      }
    }
    requestAnimationFrame(tick);
  }
  $("#profileButton").onclick = () => {
    pause();
    $("#name").value = profile.name;
    $("#profileMessage").textContent = "";
    $("#profileDialog").showModal();
  };
  $("#saveName").onclick = () => {
    profile.name = $("#name").value.trim().slice(0, 24) || "Snack explorer";
    persist();
    $("#profileMessage").textContent = storageOK ? "Nickname saved on this browser." : "Nickname changed for this session. Browser saving is unavailable.";
  };
  $("#export").onclick = () => {
    const bytes = new TextEncoder().encode(JSON.stringify(profile));
    $("#saveCode").value = "JG1." + btoa(String.fromCharCode(...bytes));
    $("#saveCode").select();
    $("#profileMessage").textContent = "Copy and keep this code. It contains completed levels and stars, not a reward balance or your current round.";
  };
  $("#import").onclick = () => {
    try {
      const text = $("#saveCode").value.trim();
      if (text.length > 12e3 || !text.startsWith("JG1.")) throw Error("Paste a valid JG1 progress code.");
      const p = validateProfile(JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(text.slice(4)), (c) => c.charCodeAt(0)))));
      if (!confirm("Replace the progress on this browser with this backup?")) return;
      profile = p;
      persist();
      clearRound();
      mode = "lobby";
      $("#play").hidden = true;
      $("#lobby").hidden = false;
      $("#profileMessage").textContent = "Progress restored. Your unlocked levels are ready.";
    } catch (e) {
      $("#profileMessage").textContent = e.message || "This progress code could not be restored.";
    }
  };
  refreshLobby();
  requestAnimationFrame(tick);
})();
